import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import Hls from "hls.js";
import "./LiveTV.css";

const LiveTV = () => {
    const [channels, setChannels] = useState([]);
    const [filteredChannels, setFilteredChannels] = useState([]);
    const [currentChannel, setCurrentChannel] = useState(null);
    const [activeCat, setActiveCat] = useState("All");

    const videoRef = useRef(null);
    const hlsRef = useRef(null);
    const switchingRef = useRef(false);

    // 🔹 Fetch Channels
    useEffect(() => {
        axios.get("https://streamsrv.n-tools.info/api/livetv/channels")
            .then(res => {
                setChannels(res.data);
                setFilteredChannels(res.data);

                if (res.data.length > 0) {
                    playChannel(res.data[0]);
                }
            })
            .catch(err => console.error(err));

        // 🧹 Cleanup on unmount
        return () => {
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
        };
    }, []);

    // 🔥 MAIN PLAYER FUNCTION (FIXED)
    const playChannel = (channel) => {
        if (switchingRef.current) return;
        switchingRef.current = true;

        const video = videoRef.current;
        if (!video) return;

        // 🧹 Destroy previous HLS
        if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
        }

        // 🧹 Reset video element
        video.pause();
        video.removeAttribute("src");
        video.load();

        setCurrentChannel(channel);

        // 🔥 HLS setup
        if (Hls.isSupported()) {
            const hls = new Hls({
                maxBufferLength: 30,
                maxMaxBufferLength: 60,
                enableWorker: true,
            });

            hlsRef.current = hls;

            hls.loadSource(channel.url);
            hls.attachMedia(video);

            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                video.play().catch(() => {});
                switchingRef.current = false;
            });

            // ⚠️ Error handling
            hls.on(Hls.Events.ERROR, (event, data) => {
                // Ignore harmless errors
                if (
                    data?.details === "bufferStalledError" ||
                    data?.details === "internalException"
                ) return;

                console.log("HLS Error:", data);

                // Auto recover
                if (data.fatal) {
                    switch (data.type) {
                        case Hls.ErrorTypes.NETWORK_ERROR:
                            hls.startLoad();
                            break;
                        case Hls.ErrorTypes.MEDIA_ERROR:
                            hls.recoverMediaError();
                            break;
                        default:
                            hls.destroy();
                            break;
                    }
                }
            });

        } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
            video.src = channel.url;
            video.play().catch(() => {});
            switchingRef.current = false;
        }

        // fallback safety
        setTimeout(() => {
            switchingRef.current = false;
        }, 800);
    };

    // 🔹 Filter Category
    const filterCategory = (cat) => {
        setActiveCat(cat);

        if (cat === "All") {
            setFilteredChannels(channels);
        } else {
            setFilteredChannels(
                channels.filter(c => c.category === cat)
            );
        }
    };

    // 🔹 Picture-in-Picture
    const togglePiP = async () => {
        try {
            if (videoRef.current !== document.pictureInPictureElement) {
                await videoRef.current.requestPictureInPicture();
            } else {
                await document.exitPictureInPicture();
            }
        } catch (err) {
            console.log("PiP Error:", err);
        }
    };

    return (
        <div className="app-container">
            {/* LEFT */}
            <div className="left-section">
                <div className="main-video-container">
                    <video
                        ref={videoRef}
                        className="video-player"
                        controls
                        autoPlay
                        playsInline
                    />
                </div>

                <div className="channel-detail-info">
                    {currentChannel && (
                        <div className="playing-now">
                            <img
                                src={currentChannel.logo}
                                alt="logo"
                                className="mini-logo"
                            />
                            <div>
                                <h3>{currentChannel.name}</h3>
                                <p>Now Streaming</p>
                            </div>
                        </div>
                    )}

                    <div className="action-btns">
                        <button
                            className="btn-icon"
                            onClick={togglePiP}
                            title="PiP"
                        >
                            🔲
                        </button>
                    </div>
                </div>
            </div>

            {/* RIGHT */}
            <div className="right-sidebar">
                <div className="sidebar-content">
                    <h2 className="section-title">Categories</h2>

                    <div className="category-grid-layout">
                        {["All", "Bangla", "Sports", "Entertainment"].map(cat => (
                            <button
                                key={cat}
                                className={`cat-btn ${activeCat === cat ? "active" : ""}`}
                                onClick={() => filterCategory(cat)}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>

                    <h2 className="section-title mt-30">Channels</h2>

                    <div className="sidebar-channel-grid">
                        {filteredChannels.map((ch, i) => (
                            <div
                                key={i}
                                className={`side-grid-item ${
                                    currentChannel?.name === ch.name ? "playing" : ""
                                }`}
                                onClick={() => playChannel(ch)}
                            >
                                <img src={ch.logo} alt={ch.name} />
                                <span className="side-name">{ch.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LiveTV;